import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import LocaleRedirectModal from 'components/Common/Modals/LocaleRedirectModal/LocaleRedirectModal'
import ModalContext from 'contexts/ModalContext'
import setLanguageCookie from 'cookies/setLanguageCookie'
import UrlLocation from 'src/lib/url/UrlLocation'
import { getVisibleLanguageOptions, resolveLocale } from 'i18n/util/locale'
import { LabRemoteConfigKeys } from 'constants/lab'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { SET_REQUEST_LOCALE } from 'actions/actionConstants'
import { syncUserDetails } from 'actions/AuthActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'

import {
  getLanguagePreferenceFromAuth,
  updateCustomerLanguagePreferenceFromAuth,
} from 'api/auth'

/**
 * Reconciles the authenticated customer language preference with the URL locale
 * after hydration. Clean URLs stay clean for auth-only preferences, explicit URL
 * locales remain the active page-language signal, and conflicts are resolved
 * through a one-per-session confirmation modal.
 */
export default function AuthBackedLocalePreferenceSync() {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  // `resolveLocaleConflict` can be triggered again while the modal is still open,
  // so keep the in-flight promise to avoid stacking duplicate modals.
  const localeRedirectModalPromiseRef = useRef<Promise<unknown> | null>(null)

  const languagesRemoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.languages)
  const languageOptions = useMemo(
    () => getVisibleLanguageOptions(languagesRemoteConfig),
    [languagesRemoteConfig],
  )

  const location = useLocation()
  const urlLocation = useMemo(() => UrlLocation.fromLocation(location), [location])
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const accountLocale = useAppSelector(state => state.auth.account.language)
  const ssrLocale = useAppSelector(state => state.geo.locale)

  const urlLocale = useMemo(
    () => resolveLocale(urlLocation.locale, languageOptions),
    [urlLocation.locale, languageOptions],
  )
  const accountLocaleState = useMemo(
    () => resolveLocale(accountLocale, languageOptions),
    [accountLocale, languageOptions],
  )

  const navigateToLocale = useCallback(
    (locale: string) => {
      const redirect = urlLocation.getRedirectToLocale(
        locale,
        languageOptions,
      )
      const targetUrl = urlLocation.withRedirect(redirect)
      if (!targetUrl.equals(urlLocation)) {
        window.location.assign(targetUrl.toString())
      }
    },
    [urlLocation, languageOptions],
  )

  const resolveLocaleConflict = useCallback(
    async(authLocale: string) => {
      if (!urlLocale.full) return

      if (localeRedirectModalPromiseRef.current) {
        await localeRedirectModalPromiseRef.current
        return
      }

      const keepPageLanguage = async() => {
        try {
          if (!urlLocation.locale || !urlLocale.full) return
          dispatch(syncUserDetails({ language: urlLocale.full }))
          await updateCustomerLanguagePreferenceFromAuth(urlLocale.full)
        } catch(error) {
          reportClientError(error)
        }
      }

      const switchToAccountLanguage = () => {
        try {
          navigateToLocale(authLocale)
        } catch(error) {
          reportClientError(error)
        }
      }

      const modalPromise = Promise.resolve(showModal(
        <LocaleRedirectModal
          urlLocale={urlLocale.full}
          accountLocale={authLocale}
          onKeepPageLanguage={keepPageLanguage}
          onSwitchToAccountLanguage={switchToAccountLanguage}
        />,
      )).finally(() => {
        if (localeRedirectModalPromiseRef.current === modalPromise) {
          localeRedirectModalPromiseRef.current = null
        }
      })

      localeRedirectModalPromiseRef.current = modalPromise
      await modalPromise
    },
    [navigateToLocale, urlLocation.locale, urlLocale.full, showModal, dispatch],
  )

  useEffect(() => {
    if (isSpoofed) return

    const abortController = new AbortController()
    const { signal } = abortController

    async function run() {
      if (signal.aborted) return
      if (!isLoggedIn) return

      if (!accountLocale) {
        const freshAccountLocale = await getLanguagePreferenceFromAuth(signal)
        if (signal.aborted) return

        const freshAccount = resolveLocale(freshAccountLocale, languageOptions)

        if (freshAccount.full) {
          if (freshAccount.normalised !== urlLocale.normalised && urlLocale.full) {
            await resolveLocaleConflict(freshAccount.full)
          }
        } else if (urlLocale.full) {
          dispatch(syncUserDetails({ language: urlLocale.full }))
        }
      } else if (accountLocaleState.full) {
        if (urlLocale.full) {
          if (accountLocaleState.normalised !== urlLocale.normalised) {
            await resolveLocaleConflict(accountLocaleState.full)
          }
        } else {
          dispatch({ type: SET_REQUEST_LOCALE, locale: accountLocaleState.full })
          if (ssrLocale && ssrLocale !== accountLocaleState.full) {
            setLanguageCookie(accountLocaleState.full)
          }
        }
      }
    }

    run()

    return () => {
      abortController.abort()
    }
  }, [
    dispatch,
    isSpoofed,
    isLoggedIn,
    languageOptions,
    accountLocale,
    urlLocale,
    accountLocaleState,
    resolveLocaleConflict,
    ssrLocale,
  ])

  return null
}
